import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

/* ============================================================================
 * Layout primitives — the panel's containers.
 * ==========================================================================*/

/**
 * PageContainer — top-level page wrapper.
 * Applies the standard 24px vertical rhythm to every direct child.
 * Optional; pages can also use `<div className="space-y-6">` directly.
 */
export const PageContainer = ({ children, className = "", testId }) => (
    <div className={`space-y-6 ${className}`} data-testid={testId}>{children}</div>
);

/**
 * Card — the standard content container.
 * Rounded-xl, hairline border, surface background. Padding is applied by
 * children (either via `p-5` wrapper or `CardHeader` + body pattern).
 */
export const Card = ({ children, className = "", ...rest }) => (
    <div className={`rounded-xl border border-border bg-surface ${className}`} {...rest}>{children}</div>
);

/**
 * CardHeader — the standardized card top row.
 * Title + optional description + optional right-aligned slot (actions/badges).
 * Bottom hairline separates from body.
 */
export const CardHeader = ({ title, desc, right, className = "" }) => (
    <div className={`px-5 pt-5 pb-3 border-b border-border flex items-start justify-between gap-3 ${className}`}>
        <div className="min-w-0">
            <h3 className="font-display text-base font-semibold tracking-tight">{title}</h3>
            {desc && <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>}
        </div>
        {right && <div className="shrink-0">{right}</div>}
    </div>
);

/**
 * SectionTitle — a standalone H2/H3 label (outside of a Card).
 * Two variants: `default` (base semibold) and `sub` (sm semibold, subordinate).
 */
export const SectionTitle = ({ children, variant = "default", className = "" }) => {
    const cls = variant === "sub"
        ? "font-display text-sm font-semibold tracking-tight"
        : "font-display text-base font-semibold tracking-tight";
    return <h2 className={`${cls} ${className}`}>{children}</h2>;
};

/* ============================================================================
 * Text primitives
 * ==========================================================================*/

/**
 * MetaText — the muted 11px meta line.
 * Use for timestamps, hints, ID copy-buttons, status footnotes.
 */
export const MetaText = ({ children, className = "" }) => (
    <span className={`text-[11px] text-muted-foreground ${className}`}>{children}</span>
);

/**
 * Eyebrow — small uppercase label above a heading or in a section header.
 * "Yeni beden serisi", "Modal ayarları", "Bedenler · sıralama" kind of labels.
 */
export const Eyebrow = ({ children, className = "" }) => (
    <div className={`text-[10px] font-semibold tracking-wider uppercase text-muted-foreground ${className}`}>
        {children}
    </div>
);

/**
 * Mono — inline monospace for IDs, SKUs, hex codes.
 */
export const Mono = ({ children, className = "" }) => (
    <span className={`font-mono text-[10px] text-muted-foreground ${className}`}>{children}</span>
);

/* ============================================================================
 * Form primitives
 * ==========================================================================*/

/**
 * Field — the canonical form-field wrapper.
 * Label above + control + optional hint below (or right-aligned as counter).
 *
 * Props:
 *   - `label`     text label (rendered as uppercase eyebrow)
 *   - `hint`      optional helper text (12px muted)
 *   - `required`  adds subtle asterisk marker
 *   - `hintRight` if true, hint renders inline with label (for character counters)
 */
export const Field = ({ label, hint, required, hintRight, children, className = "" }) => (
    <div className={`space-y-1.5 ${className}`}>
        <div className="flex items-center justify-between gap-2">
            <label className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">
                {label}
                {required && <span className="text-foreground/40 ml-0.5">*</span>}
            </label>
            {hintRight && hint && <span className="text-[10px] text-muted-foreground/70">{hint}</span>}
        </div>
        {children}
        {!hintRight && hint && <p className="text-[11px] text-muted-foreground leading-relaxed">{hint}</p>}
    </div>
);

/**
 * SwitchRow — label + description + right-aligned Switch pattern.
 * Common in settings, block editors, panel toggles.
 */
export const SwitchRow = ({ label, desc, checked, onChange, testId, disabled = false }) => (
    <div className="flex items-center justify-between gap-3 py-1.5">
        <div className="min-w-0">
            <div className="text-sm font-medium">{label}</div>
            {desc && <div className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{desc}</div>}
        </div>
        <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testId} />
    </div>
);

/**
 * TextInput — thin wrapper that guarantees the canonical Input styling.
 * Consumers can still use `<Input>` directly; this is for convenience where
 * a Field wrapper + input pair repeats often.
 */
export const TextInput = ({ className = "", ...rest }) => (
    <Input className={`h-10 text-sm ${className}`} {...rest} />
);
